import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Icon, Message } from "semantic-ui-react";

import { t } from "../../../constant/translations";
import { amountToCurrency, getAppCurrency } from "../../../helpers/currency";
import { formatDate } from "../../../helpers/date";
import {
  relFuture,
  scheduledDelta,
  serviceStatus,
} from "../../../helpers/contract-service";
import { ChangeForm } from "./change-form";
import { ConfirmModalAction } from "../../common/confirm-modal";
import { ErrorWrap } from "../../../helpers/error";
import { EditDetailsForm } from "./edit-details-form";
import { HistoryTimeline } from "./history-timeline";

const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 30px;
  border-bottom: 1px solid #e7e5e4;
`;

const BackBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid #e7e5e4;
  background: #fff;
  display: grid;
  place-items: center;
  color: #5b6068;
  cursor: pointer;
  flex: 0 0 34px;
  &:hover {
    background: #fafaf9;
    color: #1c1917;
  }
`;

const Crumb = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const CrumbMuted = styled.span`
  color: #78716c;
  cursor: pointer;
  font-size: 15px;
  &:hover {
    color: #1c1917;
  }
`;

const CrumbSep = styled.span`
  color: #78716c;
`;

const CrumbCur = styled.span`
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusPill = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: ${(props) =>
    props.kind === "active"
      ? "#e8f8ee"
      : props.kind === "scheduled"
      ? "#eaf2fd"
      : "#e9ebee"};
  color: ${(props) =>
    props.kind === "active"
      ? "#1f9a52"
      : props.kind === "scheduled"
      ? "#1d6ae0"
      : "#5b6068"};
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 22px 30px 44px;
  background: #fafbfc;
`;

const Inner = styled.div`
  width: 100%;
`;

const StateCard = styled.div`
  border: 1px solid #e9ebee;
  border-radius: 12px;
  padding: 4px 18px;
  background: #fff;
  box-shadow: 0 1px 0 rgba(20, 30, 50, 0.02);
`;

const StateHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 0 11px;
  border-bottom: 1px solid #f1f2f4;
`;

const StateLbl = styled.span`
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #78716c;
  font-weight: 700;
`;

const Kv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 0;
  border-bottom: 1px solid #f1f2f4;
  &:last-child {
    border-bottom: none;
  }
`;

const KvK = styled.span`
  color: #5b6068;
  font-size: 14px;
`;

const KvV = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1b1d21;
  ${(props) =>
    props.big && `font-size: 22px; letter-spacing: -0.3px; font-weight: 800;`}
  ${(props) => props.muted && `color: #78716c; font-weight: 600;`}
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin: 16px 0 8px;
  flex-wrap: wrap;
`;

const OneSchedNote = styled.div`
  margin-top: 14px;
  font-size: 12.5px;
  color: #5b6068;
  background: #fafaf9;
  border: 1px solid #e7e5e4;
  border-radius: 9px;
  padding: 11px 13px;
`;

const Section = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 11px;
  & h3 {
    margin: 0;
    font-size: 13px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #78716c;
  }
`;

const SchedCard = styled.div`
  border: 1px solid #e7e5e4;
  border-left: 3px solid #2185d0;
  border-radius: 10px;
  padding: 13px 14px;
  margin-bottom: 10px;
  background: linear-gradient(#fff, #fbfdff);
`;

const SchedTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SchedVal = styled.div`
  display: flex;
  align-items: baseline;
  gap: 9px;
  & .nv {
    font-size: 18px;
    font-weight: 800;
  }
  & .delta {
    font-size: 12px;
    font-weight: 700;
    color: #1d6ae0;
    background: #eaf2fd;
    padding: 2px 8px;
    border-radius: 20px;
  }
`;

const SchedWhen = styled.span`
  font-size: 12.5px;
  color: #78716c;
  font-weight: 600;
  white-space: nowrap;
`;

const SchedMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f2f4;
`;

const SchedActions = styled.div`
  display: flex;
  gap: 6px;
`;

const Empty = styled.div`
  color: #78716c;
  font-size: 13.5px;
  padding: 14px;
  border: 1px dashed #e7e5e4;
  border-radius: 10px;
  text-align: center;
`;

const NotesBlock = styled.div`
  padding: 11px 0 14px;
`;

const NotesLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #78716c;
  font-weight: 700;
  margin-bottom: 6px;
`;

const NotesBody = styled.div`
  font-size: 14px;
  color: #1b1d21;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const DangerZone = styled.div`
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px dashed #f3c8c2;
  display: flex;
  justify-content: flex-end;
`;

const ServiceId = styled.span`
  font-size: 12.5px;
  color: #78716c;
  white-space: nowrap;
`;

function formatErrorBody(err) {
  if (!err) return null;
  if (typeof err === "string") return err;
  const status = err.status;
  if (!status || status === 200) return null;
  const fields = Object.entries(err).filter(
    ([k]) => k !== "@type" && k !== "status"
  );
  if (!fields.length) return null;
  return fields
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
    .join("\n");
}

export const LinePanel = ({
  service,
  isNew,
  categories,
  onBack,
  onCreate,
  onUpdate,
  onSchedule,
  onCancelScheduled,
  onDelete,
  saving,
  error,
}) => {
  const [mode, setMode] = useState(null);

  useEffect(() => {
    setMode(null);
  }, [service && service.id, isNew]);

  if (isNew) {
    return (
      <Wrap>
        <Head>
          <BackBtn onClick={onBack} aria-label={t("contract.line.back")}>
            <Icon name="arrow left" style={{ margin: 0 }} />
          </BackBtn>
          <Crumb>
            <CrumbMuted onClick={onBack}>
              {t("contract.line.crumb_services")}
            </CrumbMuted>
            <CrumbSep>›</CrumbSep>
            <CrumbCur>{t("contract.line.new_service")}</CrumbCur>
          </Crumb>
        </Head>
        <Body>
          <Inner>
            <EditDetailsForm
              create
              categories={categories}
              onSave={onCreate}
              onCancel={onBack}
              saving={saving}
            />
          </Inner>
        </Body>
      </Wrap>
    );
  }

  if (!service) return <Wrap />;

  const status = serviceStatus(service);
  const sc = service.scheduled_change;
  const ended = status === "ended";

  return (
    <Wrap>
      <Head>
        <BackBtn onClick={onBack} aria-label={t("contract.line.back")}>
          <Icon name="arrow left" style={{ margin: 0 }} />
        </BackBtn>
        <Crumb>
          <CrumbMuted onClick={onBack}>
            {t("contract.line.crumb_services")}
          </CrumbMuted>
          <CrumbSep>›</CrumbSep>
          <CrumbCur>{service.name}</CrumbCur>
        </Crumb>
        <StatusPill kind={status}>
          {status === "ended"
            ? t("contract.line.status_ended")
            : status === "scheduled"
            ? t("contract.line.status_scheduled")
            : t("contract.line.status_active")}
        </StatusPill>
        <ServiceId>#{service.id}</ServiceId>
      </Head>

      <Body>
        <Inner>
          {formatErrorBody(error) && (
            <Message
              negative
              header={t("contract.save_error")}
              content={formatErrorBody(error)}
              style={{ whiteSpace: "pre-line", marginBottom: 14 }}
            />
          )}
          <StateCard>
            <StateHead>
              <StateLbl>
                {t("contract.line.current_state")}
                {service.category ? ` · ${service.category}` : ""}
              </StateLbl>
              {!ended && (
                <Button
                  basic
                  size="mini"
                  onClick={() => setMode("editdetails")}
                  icon
                >
                  <Icon name="edit" /> {t("contract.line.edit_details")}
                </Button>
              )}
            </StateHead>
            <Kv>
              <KvK>{t("contract.line.amount_per_month")}</KvK>
              <KvV big>
                {amountToCurrency(
                  service.amount,
                  getAppCurrency(),
                  getAppCurrency()
                )}
              </KvV>
            </Kv>
            <Kv>
              <KvK>{t("contract.line.effective_from")}</KvK>
              <KvV>
                {service.start_date ? formatDate(service.start_date) : "—"}
              </KvV>
            </Kv>
            <Kv>
              <KvK>{t("contract.line.end_label")}</KvK>
              <KvV muted={!service.end_date}>
                {service.end_date
                  ? formatDate(service.end_date)
                  : t("contract.line.no_end")}
              </KvV>
            </Kv>
            {service.notes && (
              <NotesBlock>
                <NotesLabel>{t("contract.notes")}</NotesLabel>
                <NotesBody>{service.notes}</NotesBody>
              </NotesBlock>
            )}
          </StateCard>

          {mode === null && !ended && (
            <Actions>
              {!sc ? (
                <Button primary size="small" onClick={() => setMode("plan")}>
                  <Icon name="plus" /> {t("contract.line.plan_change")}
                </Button>
              ) : (
                <Button size="small" onClick={() => setMode("editsched")}>
                  {t("contract.line.edit_scheduled")}
                </Button>
              )}
            </Actions>
          )}

          {mode === null && sc && (
            <OneSchedNote>
              {t("contract.line.scheduled_one_per_row_note")}
            </OneSchedNote>
          )}

          {mode === "plan" && (
            <div style={{ marginTop: 16 }}>
              <ChangeForm
                service={service}
                onSave={(ch) => {
                  onSchedule(ch);
                  setMode(null);
                }}
                onCancel={() => setMode(null)}
              />
            </div>
          )}

          {mode === "editsched" && (
            <div style={{ marginTop: 16 }}>
              <ChangeForm
                service={service}
                initial={sc}
                editing
                onSave={(ch) => {
                  onSchedule(ch);
                  setMode(null);
                }}
                onCancel={() => setMode(null)}
              />
            </div>
          )}

          {mode === "editdetails" && (
            <div style={{ marginTop: 16 }}>
              <EditDetailsForm
                service={service}
                categories={categories}
                onSave={(f) => {
                  onUpdate(f);
                  setMode(null);
                }}
                onCancel={() => setMode(null)}
                saving={saving}
              />
            </div>
          )}

          <Section>
            <SectionTitle>
              <h3>{t("contract.line.scheduled_title")}</h3>
            </SectionTitle>
            {sc ? (
              <SchedCard>
                <SchedTop>
                  <SchedVal>
                    {sc.type === "end" ? (
                      <span className="nv" style={{ color: "#b23a29" }}>
                        {t("contract.line.change_type_end")}
                      </span>
                    ) : (
                      <>
                        <span className="nv">
                          {amountToCurrency(
                            sc.new_amount,
                            getAppCurrency(),
                            getAppCurrency()
                          )}
                        </span>
                        <span className="delta">{scheduledDelta(sc)}</span>
                      </>
                    )}
                  </SchedVal>
                  <SchedWhen>
                    {formatDate(sc.effective_from)} ·{" "}
                    {relFuture(sc.effective_from)}
                  </SchedWhen>
                </SchedTop>
                {sc.note && (
                  <div style={{ fontSize: 13, color: "#5b6068", marginTop: 7 }}>
                    {sc.note}
                  </div>
                )}
                <SchedMeta>
                  <span style={{ fontSize: 12.5, color: "#5b6068" }}>
                    {sc.created_at ? formatDate(sc.created_at) : ""}
                  </span>
                  <SchedActions>
                    <Button
                      basic
                      icon
                      size="mini"
                      onClick={() => setMode("editsched")}
                    >
                      <Icon name="edit" />
                    </Button>
                    <Button
                      basic
                      negative
                      icon
                      size="mini"
                      onClick={onCancelScheduled}
                    >
                      <Icon name="close" />
                    </Button>
                  </SchedActions>
                </SchedMeta>
              </SchedCard>
            ) : (
              <Empty>{t("contract.line.scheduled_empty")}</Empty>
            )}
          </Section>

          <Section>
            <SectionTitle>
              <h3>{t("contract.line.history_title")}</h3>
            </SectionTitle>
            <HistoryTimeline serviceId={service.id} />
          </Section>

          {onDelete && (
            <DangerZone>
              <ConfirmModalAction
                header={t("contract.line.delete_header")}
                content={t("contract.line.delete_warning")}
                confirmLabel={t("contract.line.delete_confirm")}
                action={onDelete}
                loading={!!saving}
                error={new ErrorWrap(null)}
                trigger_props={{
                  negative: true,
                  basic: true,
                  icon: "trash",
                  content: t("contract.line.delete_btn"),
                  size: "small",
                }}
              />
            </DangerZone>
          )}
        </Inner>
      </Body>
    </Wrap>
  );
};
